import { z } from 'zod';

/**
 * Every error response from the API has this shape:
 * `{ "error": { "code": "NOT_FOUND", "message": "Group not found", "details": … } }`.
 * `code` is stable and meant for programmatic handling (and i18n on the client);
 * `message` is English, for logs and developers only.
 */
export const ERROR_CODES = [
  /** Malformed request (bad JSON, wrong content type). 400 */
  'BAD_REQUEST',
  /**
   * Body, query or params failed schema or domain validation. `details` has the issues
   * (`{ issues: ValidationIssue[] }`), or for member checks `RemovedMembersDetails`. 422
   */
  'VALIDATION_FAILED',
  /** No valid session. 401 */
  'UNAUTHENTICATED',
  /** Signed in but not allowed (e.g. not the group admin, not the app admin). 403 */
  'FORBIDDEN',
  /** The account was disabled by the admin. All sessions are revoked. 403 */
  'ACCOUNT_DISABLED',
  /** The cross-site request check failed (missing/foreign Origin on a write). 403 */
  'CSRF_REJECTED',
  /** Resource does not exist or the caller cannot see it (never leaks existence). 404 */
  'NOT_FOUND',
  /** State conflict (e.g. already a member, settlement no longer pending). 409 */
  'CONFLICT',
  /** Optimistic concurrency: `expectedVersion` does not match. `details.currentVersion`. 409 */
  'VERSION_CONFLICT',
  /** The member still owes or is owed money, so they cannot leave or be removed. 409 */
  'BALANCE_NOT_ZERO',
  /** A payment to or from the member still awaits confirmation, so they cannot leave or be removed. 409 */
  'SETTLEMENT_PENDING',
  /** Too many attempts. `Retry-After` header is set. 429 */
  'RATE_LIMITED',
  /** `POST /api/setup` was already used. 409 */
  'SETUP_ALREADY_DONE',
  /** The setup secret is wrong. 403 */
  'SETUP_SECRET_INVALID',
  /** Invite code unknown or revoked (indistinguishable on purpose). 404 */
  'INVITE_INVALID',
  /** Invite already redeemed. 410 */
  'INVITE_USED',
  /** Invite expired. 410 */
  'INVITE_EXPIRED',
  /** The recovery phrase does not match an account (or is malformed). 401 */
  'RECOVERY_INVALID',
  /** WebAuthn ceremony failed or the challenge expired. 400 */
  'PASSKEY_FAILED',
  /** No exchange rate is available for the requested currency pair/date. 422 */
  'FX_UNAVAILABLE',
  /** Upload too large. 413 */
  'PAYLOAD_TOO_LARGE',
  /** Upload content type not accepted. 415 */
  'UNSUPPORTED_MEDIA_TYPE',
  /** Feature configured off (e.g. push without VAPID keys). 503 */
  'NOT_CONFIGURED',
  /** Unexpected server error. 500 */
  'INTERNAL',
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/** One validation issue inside `details.issues` for `VALIDATION_FAILED`. */
export const validationIssueSchema = z.object({
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
  code: z.string(),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

/**
 * `details` of a `VALIDATION_FAILED` (422) refused because of group members who left
 * (removed or merged):
 * - creating an expense with someone who is not an active member;
 * - editing an expense in a way that changes what someone who left paid minus owed;
 * - deleting (`DELETE /api/expenses/:id`) or restoring (`POST /api/expenses/:id/restore`)
 *   an expense on which someone who left has a non-zero paid minus owed.
 * `memberIds` are the members that block the write.
 */
export const removedMembersDetailsSchema = z.object({ memberIds: z.array(z.string()) });
export type RemovedMembersDetails = z.infer<typeof removedMembersDetailsSchema>;

/** `details.reason` of a `CONFLICT` from `POST /api/settlements/:id/undo-reject`. */
export const undoRejectConflictReasonSchema = z.enum([
  'NOTHING_TO_UNDO',
  'WINDOW_PASSED',
  'MEMBER_LEFT',
]);
export type UndoRejectConflictReason = z.infer<typeof undoRejectConflictReasonSchema>;

/** Default HTTP status for each error code. */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  BAD_REQUEST: 400,
  VALIDATION_FAILED: 422,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  ACCOUNT_DISABLED: 403,
  CSRF_REJECTED: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VERSION_CONFLICT: 409,
  BALANCE_NOT_ZERO: 409,
  SETTLEMENT_PENDING: 409,
  RATE_LIMITED: 429,
  SETUP_ALREADY_DONE: 409,
  SETUP_SECRET_INVALID: 403,
  INVITE_INVALID: 404,
  INVITE_USED: 410,
  INVITE_EXPIRED: 410,
  RECOVERY_INVALID: 401,
  PASSKEY_FAILED: 400,
  FX_UNAVAILABLE: 422,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  NOT_CONFIGURED: 503,
  INTERNAL: 500,
};
